import React, { createContext, useState, useEffect } from 'react';
import { APP_CONFIG } from "../config/appConfig";
import tracker from '../utils/tracker';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const BASE_URL = APP_CONFIG.API_BASE_URL;

  // Backward-compatible wrapper: components still call reportActivity around
  // important actions. It now delegates to the behavior tracker, which
  // refreshes lastActivity via a throttled heartbeat.
  const reportActivity = () => {
    try {
      tracker.touch();
    } catch {
      // tracking must never break the app
    }
  };

  const verifyAuth = async () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');

    if (!isAuthenticated) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/login/verify-token`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          uid: data.uid,
          email: data.user.email,
          ...data.user
        });

        reportActivity();
      } else {
        localStorage.removeItem('isAuthenticated');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('isAuthenticated');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    localStorage.setItem('isAuthenticated', 'true');
    setUser(userData);
    // flush anonymous events so they stay attributed to the pre-login identity
    try { tracker.onAuthChange(); } catch {}
  };

  // Optimistic merge for updating a few fields without a full verify-token round-trip
  const updateUser = (partialData) => {
    setUser((prev) => (prev ? { ...prev, ...partialData } : prev));
  };

  const logout = async () => {
    // capture the session id BEFORE onAuthChange rotates it, so the backend
    // can mark this session as ended
    let sessionId = null;
    try { sessionId = tracker.getSessionId(); } catch {}
    try { tracker.onAuthChange(); } catch {}
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('isAuthenticated');
      setUser(null);
    }
  };

  useEffect(() => {
    verifyAuth();
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      updateUser,
      refreshAuth: verifyAuth,
      reportActivity, // expose so any component can call it for important actions
    }}>
      {children}
    </AuthContext.Provider>
  );
};
